import { useQuery } from '@tanstack/react-query';
import { adminRpc } from '@/integrations/supabase/rpc';
import { AdminPayout } from '@/types/admin';
import type { UseListResult, Pagination, Sort } from './types';

export type PayoutFilters = {
  customerId?: string | null;
  status?: string | null;
  dateFrom?: string | null;
  dateTo?: string | null;
  search?: string | null;
  bridgeCustomerId?: string | null;
};

/**
 * Hook for fetching admin payouts with pagination, sorting, and filtering
 * 
 * @param pagination - { limit, offset } for pagination
 * @param sort - { key, dir } for sorting (default: created_at desc)
 * @param filters - Optional filters for customerId, status, dates, etc.
 * @returns { rows, total, pageCount, isLoading, isError, error, refetch }
 * 
 * @example
 * ```ts
 * const { rows, total, isLoading } = useAdminPayouts(
 *   { limit: 20, offset: 0 },
 *   { key: 'settled_at', dir: 'desc' },
 *   { customerId: 'uuid', status: 'settled' }
 * );
 * ```
 */
export function useAdminPayouts(
  { limit, offset }: Pagination,
  sort: Sort = { key: 'created_at', dir: 'desc' },
  filters: PayoutFilters = {}
): UseListResult<AdminPayout> {
  const key = ['admin', 'payouts', { limit, offset, sort, filters }];
  
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: key,
    queryFn: async () => {
      const res = await adminRpc.listPayouts({
        p_limit: limit,
        p_offset: offset,
        p_customer_id: filters.customerId ?? null,
        p_status: filters.status ?? null,
        p_sort: `${sort.key}:${sort.dir}`,
        p_date_from: filters.dateFrom ?? null,
        p_date_to: filters.dateTo ?? null,
        p_search: filters.search ?? null,
        p_bridge_customer_id: filters.bridgeCustomerId ?? null,
      });
      return { rows: res.data, total: res.total };
    },
    retry: 1,
    staleTime: 5000,
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const pageCount = Math.ceil(total / Math.max(1, limit));

  return { rows, total, pageCount, isLoading, isError, error, refetch };
}